import { mapGlobalSearchResults, type GlobalSearchResult } from '@/hooks/globalSearchResults';
import { apiMethods } from '@/services/api';
import { unwrapApiArray } from '@/utils/apiResponse';
import { useCallback,useRef,useState } from 'react';
import { useNavigate } from 'react-router';

export function useSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GlobalSearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const search = useCallback((searchQuery: string) => {
    setQuery(searchQuery);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!searchQuery.trim()) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const [itemsRes, recipesRes] = await Promise.all([
          apiMethods.items.list({ search: searchQuery, take: 5 }),
          apiMethods.recipes.list({ search: searchQuery, take: 5 }),
        ]);

        const itemsData = unwrapApiArray<{ id: string; name: string; sku: string; category?: string }>(itemsRes);
        const recipesData = unwrapApiArray<{ id: string; name: string; sku: string; components?: unknown[] }>(recipesRes);
        const mapped = mapGlobalSearchResults(itemsData, recipesData);

        setResults(mapped);
        setIsOpen(mapped.length > 0);
      } catch {
        // On API error, clear results silently
        setResults([]);
        setIsOpen(false);
      }
    }, 250);
  }, []);

  const selectResult = useCallback(
    (result: GlobalSearchResult) => {
      navigate(result.path);
      setQuery('');
      setResults([]);
      setIsOpen(false);
    },
    [navigate]
  );

  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
  }, []);

  return {
    query,
    results,
    isOpen,
    search,
    selectResult,
    clearSearch,
    setIsOpen,
  };
}
